import { useCallback, useMemo } from 'react';
import { useBusinessMetrics } from './useStatsigFeatures';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'NotificationAnalytics', level: 'info' });

interface NotificationMetadata {
  notification_id: string;
  notification_type?: 'info' | 'success' | 'warning' | 'error';
  delivery_method?: 'banner' | 'bell' | 'email' | 'push';
  category?: string;
  template_key?: string;
  priority?: number;
  target_page?: string;
  [key: string]: any;
}

interface InteractionMetadata extends NotificationMetadata {
  time_to_interaction_ms?: number;
  interaction_context?: 'panel' | 'banner' | 'navigation_dot';
  notification_age_ms?: number;
  action_type?: string;
  resulted_in_navigation?: boolean;
  navigation_target?: string;
}

interface SystemMetadata {
  connection_attempt?: number;
  time_to_connect_ms?: number;
  connection_duration_ms?: number;
  fallback_active?: boolean;
  cleanup_count?: number;
  overflow_count?: number;
  error_type?: string;
  error_message?: string;
  [key: string]: any;
}

interface FeatureUsageMetadata {
  panel_state?: 'active' | 'history';
  unread_count?: number;
  total_count?: number;
  settings_changed?: string[];
  carousel_position?: number;
  expanded_notification_id?: string;
  [key: string]: any;
}

/**
 * Hook for tracking notification system analytics
 */
export function useNotificationAnalytics() {
  const { trackFeatureUsage, trackBusinessMetric } = useBusinessMetrics();

  // Use useMemo to create stable tracking functions that won't cause re-renders
  const stableTrackers = useMemo(() => {
    // Notification Delivery Events
    const trackNotificationCreated = (metadata: NotificationMetadata) => {
      trackFeatureUsage('notification_system', 'notification_created', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked notification created', metadata);
    };

    const trackNotificationDelivered = (metadata: NotificationMetadata & {
      user_has_unread?: boolean;
      total_unread_count?: number;
    }) => {
      trackFeatureUsage('notification_system', 'notification_delivered', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked notification delivered', metadata);
    };

    const trackNotificationFailed = (metadata: NotificationMetadata & {
      error_type: string;
      error_message?: string;
    }) => {
      trackFeatureUsage('notification_system', 'notification_failed', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.error('Tracked notification failure', metadata);
    };

    // User Interaction Events
    const trackNotificationViewed = (metadata: InteractionMetadata) => {
      trackFeatureUsage('notification_system', 'notification_viewed', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked notification viewed', metadata);
    };

    const trackNotificationRead = (metadata: InteractionMetadata & {
      read_method?: 'manual' | 'auto';
    }) => {
      trackFeatureUsage('notification_system', 'notification_read', {
        ...metadata,
        timestamp: Date.now()
      });
      
      // Track business metric for engagement
      if (metadata.time_to_interaction_ms) {
        trackBusinessMetric('notification_time_to_read', metadata.time_to_interaction_ms, {
          delivery_method: metadata.delivery_method,
          category: metadata.category
        });
      }
      
      logger.debug('Tracked notification read', metadata);
    };

    const trackNotificationClicked = (metadata: InteractionMetadata) => {
      trackFeatureUsage('notification_system', 'notification_clicked', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked notification clicked', metadata);
    };

    const trackNotificationDismissed = (metadata: InteractionMetadata) => {
      trackFeatureUsage('notification_system', 'notification_dismissed', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked notification dismissed', metadata);
    };

    const trackNotificationActionClicked = (metadata: InteractionMetadata & {
      action_url?: string;
      action_text?: string;
    }) => {
      trackFeatureUsage('notification_system', 'notification_action_clicked', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked notification action clicked', metadata);
    };

    // System Performance Events
    const trackRealtimeConnected = (metadata: SystemMetadata) => {
      trackFeatureUsage('notification_system', 'realtime_connected', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.info('Tracked realtime connection', metadata);
    };

    const trackRealtimeDisconnected = (metadata: SystemMetadata) => {
      trackFeatureUsage('notification_system', 'realtime_disconnected', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.warn('Tracked realtime disconnection', metadata);
    };

    const trackRealtimeFallback = (metadata: SystemMetadata) => {
      trackFeatureUsage('notification_system', 'realtime_fallback', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.warn('Tracked realtime fallback', metadata);
    };

    const trackCleanupExecuted = (metadata: SystemMetadata) => {
      trackFeatureUsage('notification_system', 'cleanup_executed', {
        ...metadata,
        timestamp: Date.now()
      });
      
      // Track business metric for cleanup efficiency
      if (metadata.cleanup_count) {
        trackBusinessMetric('notification_cleanup_count', metadata.cleanup_count);
      }
      
      logger.info('Tracked cleanup execution', metadata);
    };

    const trackOverflowTriggered = (metadata: SystemMetadata) => {
      trackFeatureUsage('notification_system', 'overflow_triggered', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.warn('Tracked overflow trigger', metadata);
    };

    // Feature Usage Events
    const trackPanelOpened = (metadata: FeatureUsageMetadata) => {
      trackFeatureUsage('notification_panel', 'opened', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked panel opened', metadata);
    };

    const trackPanelTabSwitched = (metadata: FeatureUsageMetadata & {
      from_tab: 'active' | 'history';
      to_tab: 'active' | 'history';
    }) => {
      trackFeatureUsage('notification_panel', 'tab_switched', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked panel tab switch', metadata);
    };

    const trackSettingsUpdated = (metadata: FeatureUsageMetadata) => {
      trackFeatureUsage('notification_settings', 'updated', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked settings update', metadata);
    };

    const trackBannerCarouselNavigated = (metadata: FeatureUsageMetadata & {
      direction: 'next' | 'previous';
      total_banners: number;
    }) => {
      trackFeatureUsage('notification_banner', 'carousel_navigated', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked banner carousel navigation', metadata);
    };

    const trackTextExpanded = (metadata: FeatureUsageMetadata) => {
      trackFeatureUsage('notification_panel', 'text_expanded', {
        ...metadata,
        timestamp: Date.now()
      });
      logger.debug('Tracked text expansion', metadata);
    };

    // Business Metrics Calculations
    const trackEngagementMetrics = (metrics: {
      delivered_count: number;
      read_count: number;
      action_count: number;
      dismissed_count: number;
      period?: 'daily' | 'weekly' | 'monthly';
    }) => {
      const engagementRate = metrics.delivered_count > 0 
        ? (metrics.read_count / metrics.delivered_count) * 100 
        : 0;
      
      const actionRate = metrics.read_count > 0
        ? (metrics.action_count / metrics.read_count) * 100
        : 0;
      
      const dismissalRate = metrics.delivered_count > 0
        ? (metrics.dismissed_count / metrics.delivered_count) * 100
        : 0;

      trackBusinessMetric('notification_engagement_rate', engagementRate, {
        period: metrics.period || 'daily',
        delivered_count: metrics.delivered_count,
        read_count: metrics.read_count
      });

      trackBusinessMetric('notification_action_rate', actionRate, {
        period: metrics.period || 'daily',
        action_count: metrics.action_count,
        read_count: metrics.read_count
      });

      trackBusinessMetric('notification_dismissal_rate', dismissalRate, {
        period: metrics.period || 'daily',
        dismissed_count: metrics.dismissed_count,
        delivered_count: metrics.delivered_count
      });

      logger.info('Tracked engagement metrics', {
        engagementRate,
        actionRate,
        dismissalRate,
        ...metrics
      });
    };

    const trackUnreadAccumulation = (unreadCount: number, metadata?: {
      by_category?: Record<string, number>;
      oldest_unread_age_ms?: number;
    }) => {
      trackBusinessMetric('notification_unread_accumulation', unreadCount, metadata);
      logger.debug('Tracked unread accumulation', { unreadCount, ...metadata });
    };

    return {
      // Delivery Events
      trackNotificationCreated,
      trackNotificationDelivered,
      trackNotificationFailed,
      
      // Interaction Events
      trackNotificationViewed,
      trackNotificationRead,
      trackNotificationClicked,
      trackNotificationDismissed,
      trackNotificationActionClicked,
      
      // System Performance
      trackRealtimeConnected,
      trackRealtimeDisconnected,
      trackRealtimeFallback,
      trackCleanupExecuted,
      trackOverflowTriggered,
      
      // Feature Usage
      trackPanelOpened,
      trackPanelTabSwitched,
      trackSettingsUpdated,
      trackBannerCarouselNavigated,
      trackTextExpanded,
      
      // Business Metrics
      trackEngagementMetrics,
      trackUnreadAccumulation
    };
  }, [trackFeatureUsage, trackBusinessMetric]);

  return stableTrackers;
}